import { Chart as ChartJS, BarElement, CategoryScale, LinearScale, Tooltip } from "chart.js";
import millify from "millify";
import { useCallback, useEffect, useState } from "react";
import { Bar } from "react-chartjs-2";
import { useTranslation } from "react-i18next";

import { LinkButton } from "../../components/Button";
import { Box } from "../../components/Layout";
import { LoadingModal } from "../../components/LoadingModal";
import { SubHeader } from "../../components/SubHeader";
import Tabs, { TabsContent, TabsHeader, TabsNavItem, TabsPane } from "../../components/Tabs";
import { useAppState } from "../../contexts/App";
import { useSafeGoBack } from "../../hooks/SafeGoBack";
import { api } from "../../services/api";
import { AnalyticsSummary } from "../../services/api/models";
import { classNames } from "../../utils/styles";
import { BigsNumbers } from "./components/BigNumbers";
import { LockedPreview } from "./components/LockedPreview";
import { PeriodSelection } from "./components/PeriodSelection";
import { SummaryList } from "./components/SummaryList";
import { EmptyStateIcon } from "./icons/EmptyState";
import {
  Container,
  EmptyStateBox,
  FooterBox,
  StatisticsContent,
  StatisticsContentBars,
  StatisticsContentBody,
  StatisticsContentHead,
  StatisticsContentSubTabs,
} from "./styles";
import { useBarOptions, useDataPresentation, useMetrics, useMillifyOptions, usePeriods } from "./utils";

// Tooltip: the day's total on a column under the mouse (barOptions' tooltip needs it registered)
ChartJS.register(BarElement, CategoryScale, LinearScale, Tooltip);

const StatisticsPage = () => {
  const { t } = useTranslation();
  const safeGoBack = useSafeGoBack();

  const { appState } = useAppState();

  const [dataLoaded, setDataLoaded] = useState(false);
  const [loadingPresentation, setLoadingPresentation] = useState(true);

  const [activeMetric, setActiveMetric] = useState(0);
  const [activePeriod, setActivePeriod] = useState(0);

  const [dataSummary, setDataSummary] = useState<AnalyticsSummary>();

  const millifyOptions = useMillifyOptions();
  const barOptions = useBarOptions(millifyOptions);

  const metrics = useMetrics();
  const periods = usePeriods();
  const isPlus = appState.editingSiteSubscription?.type === "PLUS";

  const dataPresentation = useDataPresentation(
    metrics,
    periods,
    activeMetric,
    activePeriod,
    dataLoaded,
    setLoadingPresentation,
    dataSummary
  );

  const getFormattedNumber = useCallback((value: number) => millify(value, millifyOptions), [millifyOptions]);

  const loadDataHandler = useCallback(async () => {
    if (!isPlus) {
      setLoadingPresentation(false);
      return;
    }

    const data = await api.getAnalytics(appState.editingSiteId as string);
    setDataSummary(data);
    setDataLoaded(true);
  }, [appState.editingSiteId, isPlus]);

  useEffect(() => {
    loadDataHandler();
  }, [loadDataHandler]);

  return loadingPresentation ? (
    <LoadingModal />
  ) : (
    <Container>
      <SubHeader title="Analytics" crumbs={[]} />
      <Box>
        <Tabs>
          <TabsHeader>
            {metrics.map((metric, index) => (
              <TabsNavItem key={index} active={activeMetric === index} onClick={() => setActiveMetric(index)}>
                {metric.title}
              </TabsNavItem>
            ))}
          </TabsHeader>
          <TabsContent>
            {!isPlus ? (
              <TabsPane className="show">
                <StatisticsContentHead>
                  <PeriodSelection periods={periods} activePeriod={activePeriod} onSelect={setActivePeriod} />
                </StatisticsContentHead>
                <LockedPreview
                  metricType={metrics[activeMetric].type}
                  days={periods[activePeriod].bars}
                  siteId={appState.editingSiteId}
                />
              </TabsPane>
            ) : (
              metrics.map((_, index) => (
                <TabsPane
                  className={classNames({
                    show: activeMetric === index,
                  })}
                  key={index}>
                  {!dataPresentation ? (
                    <EmptyStateBox>
                      <EmptyStateIcon />
                      <p>{t(`pages.analytics.emptyState.${metrics[activeMetric].type}`)}</p>
                    </EmptyStateBox>
                  ) : (
                    <>
                      <StatisticsContentHead>
                        <PeriodSelection periods={periods} activePeriod={activePeriod} onSelect={setActivePeriod} />
                      </StatisticsContentHead>
                      <StatisticsContentSubTabs>
                        {periods.map((_, idx) => (
                          <StatisticsContent
                            key={idx}
                            className={classNames({
                              show: activePeriod === idx,
                            })}>
                            {dataPresentation.unavailable ? (
                              <EmptyStateBox>
                                <EmptyStateIcon />
                                <p>{t("pages.analytics.periodUnavailable")}</p>
                              </EmptyStateBox>
                            ) : (
                              <StatisticsContentBody>
                                <BigsNumbers
                                  formatterNumber={getFormattedNumber}
                                  numbers={dataPresentation.bigNumbers}
                                />
                                {dataPresentation.chart && (
                                  <StatisticsContentBars>
                                    <Bar
                                      //@ts-ignore
                                      data={dataPresentation.chart}
                                      //@ts-ignore
                                      options={barOptions}
                                      type=""
                                    />
                                  </StatisticsContentBars>
                                )}
                                <SummaryList
                                  title={metrics[activeMetric].listDescription}
                                  values={dataPresentation.list}
                                  formatterNumber={getFormattedNumber}
                                />
                              </StatisticsContentBody>
                            )}
                          </StatisticsContent>
                        ))}
                      </StatisticsContentSubTabs>
                    </>
                  )}
                </TabsPane>
              ))
            )}
          </TabsContent>
        </Tabs>
      </Box>
      {dataPresentation && (
        <FooterBox>
          <LinkButton onClick={safeGoBack} className="go-back-button">
            {t("actions.goBack")}
          </LinkButton>
        </FooterBox>
      )}
    </Container>
  );
};

export default StatisticsPage;
